/**
 * O que faz: Renderiza o relatório completo de SEO de perfil em slides estilizados pela identidade Gens.
 * Onde usa: SeoProfileExportDialog (modal de exportação e impressão).
 * Conexões: Recebe SeoProfileAnalysisResult e produz slides 1920x1080 (wide) ou 1080x1920 (vertical).
 * Tabelas: Não se aplica — apenas renderização.
 * Data: 14/05/2026
 */

import type {
  SeoDimensionStatus,
  SeoProfileAnalysisResult,
  SeoStructuredAnalysis,
  SeoStructuredDimension,
} from '@/types/seoProfile';

export type ReportFormat = 'wide' | 'vertical';

interface SeoProfileReportProps {
  analysis: SeoProfileAnalysisResult;
  format: ReportFormat;
  clinicName?: string;
}

const GENS = {
  oliveDark: '#3B4A1C',
  oliveMid: '#5C6B2F',
  limeLight: '#E8F0C8',
  limeVivid: '#D5E946',
  white: '#FFFFFF',
  black: '#1A1A1A',
  red: '#C53030',
  amber: '#D69E2E',
} as const;

const DIMS = {
  wide: { w: 1920, h: 1080 },
  vertical: { w: 1080, h: 1920 },
} as const;

const sourceLabel: Record<string, string> = {
  website: 'Site',
  google_business: 'Google Meu Negócio',
  instagram: 'Instagram',
  doctoralia: 'Doctoralia',
  general_search: 'Busca geral',
};

const dimensionLabel: Record<string, string> = {
  categorias: 'Categorias',
  informacoes_basicas: 'Nome, endereço e telefone',
  descricao: 'Descrição do perfil',
  servicos: 'Serviços cadastrados',
  atributos: 'Atributos e comodidades',
  reviews_volume_qualidade: 'Avaliações — volume e qualidade',
  respostas_reviews: 'Respostas às avaliações',
  posts: 'Publicações no perfil',
  fotos: 'Fotos',
  qa: 'Perguntas e respostas',
  perguntas_e_respostas: 'Perguntas e respostas',
  recursos_avancados: 'Recursos avançados',
  seo_tecnico: 'SEO técnico',
  seo_local: 'SEO local',
  conversao: 'Conversão',
  schema_e_dados_estruturados: 'Dados estruturados',
  ai_seo: 'Otimização para IA',
  conteudo_e_clareza: 'Conteúdo e clareza',
  posicionamento: 'Posicionamento',
  bio_e_cta: 'Bio e chamada para ação',
  prova_social: 'Prova social',
  conteudo: 'Conteúdo',
  completude_do_perfil: 'Completude do perfil',
  especialidade_e_posicionamento: 'Especialidade e posicionamento',
  agendamento: 'Agendamento',
  identificacao_da_clinica: 'Identificação da clínica',
  presenca_local: 'Presença local',
  reputacao_publica: 'Reputação pública',
  lacunas_de_dados: 'Informações em falta',
  proximas_analises_recomendadas: 'Próximas análises recomendadas',
  dados_ausentes: 'Dados não encontrados',
};

const statusMeta: Record<SeoDimensionStatus, { label: string; color: string; bg: string }> = {
  critico: { label: 'CRÍTICO', color: GENS.white, bg: GENS.red },
  atencao: { label: 'ATENÇÃO', color: GENS.oliveDark, bg: GENS.limeVivid },
  ok: { label: 'OK', color: GENS.white, bg: GENS.oliveMid },
  excelente: { label: 'EXCELENTE', color: GENS.white, bg: GENS.oliveDark },
};

function scoreLevel(score: number): { label: string; color: string } {
  if (score >= 70) return { label: 'Boa presença digital', color: GENS.limeVivid };
  if (score >= 40) return { label: 'Presença em desenvolvimento', color: GENS.limeVivid };
  return { label: 'Baixa presença digital', color: GENS.limeVivid };
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return '';
  }
}

function dimensionTitle(value: string): string {
  return dimensionLabel[value] ?? value.replace(/_/g, ' ');
}

interface SlideBaseProps {
  format: ReportFormat;
  background: string;
  textColor: string;
  children: React.ReactNode;
  pageNumber?: number;
  totalPages?: number;
}

function Slide({ format, background, textColor, children, pageNumber, totalPages }: SlideBaseProps) {
  const dim = DIMS[format];
  return (
    <section
      className="seo-slide"
      style={{
        width: `${dim.w}px`,
        height: `${dim.h}px`,
        background,
        color: textColor,
        position: 'relative',
        overflow: 'hidden',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        pageBreakAfter: 'always',
        breakAfter: 'page',
        pageBreakInside: 'avoid',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: format === 'wide' ? 48 : 64,
          left: format === 'wide' ? 64 : 64,
          right: format === 'wide' ? 64 : 64,
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: format === 'wide' ? 20 : 26,
          fontWeight: 600,
          letterSpacing: '0.03em',
          opacity: 0.8,
          zIndex: 10,
        }}
      >
        <span>Agência Gens</span>
        <span>@agencia.gens</span>
      </div>

      {children}

      {pageNumber && totalPages && (
        <div
          style={{
            position: 'absolute',
            bottom: format === 'wide' ? 36 : 48,
            right: format === 'wide' ? 64 : 64,
            fontSize: format === 'wide' ? 16 : 22,
            fontWeight: 500,
            opacity: 0.5,
            zIndex: 10,
          }}
        >
          {pageNumber}/{totalPages}
        </div>
      )}
    </section>
  );
}

function CoverSlide({ analysis, format, clinicName, pageNumber, totalPages }: { analysis: SeoProfileAnalysisResult; format: ReportFormat; clinicName?: string; pageNumber: number; totalPages: number }) {
  const score = analysis.overall_score ?? 0;
  const lvl = scoreLevel(score);
  const source = analysis.analysis_source ? (sourceLabel[analysis.analysis_source] ?? analysis.analysis_source) : null;
  const date = formatDate(analysis.created_at);

  const isWide = format === 'wide';

  return (
    <Slide
      format={format}
      background={`linear-gradient(155deg, #1a2a08 0%, ${GENS.oliveDark} 55%, ${GENS.oliveMid} 100%)`}
      textColor={GENS.white}
      pageNumber={pageNumber}
      totalPages={totalPages}
    >
      {/* Decorative blob */}
      <div
        style={{
          position: 'absolute',
          width: isWide ? 600 : 700,
          height: isWide ? 600 : 700,
          background: GENS.limeVivid,
          opacity: 0.12,
          borderRadius: '50%',
          top: isWide ? -120 : -200,
          right: isWide ? -150 : -200,
          filter: 'blur(120px)',
        }}
      />

      <div
        style={{
          position: 'absolute',
          top: isWide ? '50%' : '50%',
          left: isWide ? 120 : 64,
          right: isWide ? 120 : 64,
          transform: 'translateY(-50%)',
          display: 'flex',
          flexDirection: isWide ? 'row' : 'column',
          alignItems: isWide ? 'center' : 'flex-start',
          gap: isWide ? 100 : 60,
        }}
      >
        {/* Score gigante */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: isWide ? 'center' : 'flex-start' }}>
          <p style={{ fontSize: isWide ? 18 : 26, fontWeight: 600, color: GENS.limeVivid, letterSpacing: '0.12em', marginBottom: 4 }}>
            PONTUAÇÃO GERAL
          </p>
          <p style={{ fontSize: isWide ? 320 : 380, fontWeight: 800, color: GENS.limeVivid, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
            {score}
          </p>
          <p style={{ fontSize: isWide ? 22 : 30, fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginTop: -8 }}>
            de 100
          </p>
        </div>

        {/* Lado direito */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: isWide ? 18 : 26, fontWeight: 600, color: GENS.limeVivid, letterSpacing: '0.12em', marginBottom: 24 }}>
            DIAGNÓSTICO DE PRESENÇA DIGITAL
          </p>
          <h1 style={{ fontSize: isWide ? 72 : 88, fontWeight: 800, lineHeight: 1.05, marginBottom: 32 }}>
            {clinicName ?? 'Como sua clínica'}
            <br />
            <span style={{ color: GENS.limeVivid }}>aparece online?</span>
          </h1>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 32 }}>
            {source && (
              <span style={{ display: 'inline-block', background: 'rgba(255,255,255,0.10)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.2)', padding: isWide ? '8px 20px' : '12px 28px', borderRadius: 999, fontSize: isWide ? 18 : 26, fontWeight: 600 }}>
                {source}
              </span>
            )}
            <span style={{ display: 'inline-block', background: lvl.color, color: GENS.oliveDark, padding: isWide ? '8px 20px' : '12px 28px', borderRadius: 999, fontSize: isWide ? 18 : 26, fontWeight: 700 }}>
              {lvl.label}
            </span>
          </div>

          {analysis.summary && (
            <p style={{ fontSize: isWide ? 22 : 30, fontWeight: 400, color: 'rgba(255,255,255,0.85)', lineHeight: 1.5, maxWidth: isWide ? 720 : '100%' }}>
              {analysis.summary}
            </p>
          )}

          {date && (
            <p style={{ fontSize: isWide ? 14 : 20, fontWeight: 600, color: 'rgba(255,255,255,0.5)', marginTop: 40, letterSpacing: '0.06em' }}>
              ANÁLISE GERADA EM {date.toUpperCase()}
            </p>
          )}
        </div>
      </div>
    </Slide>
  );
}

function HighlightSlide({ format, kind, items, pageNumber, totalPages }: { format: ReportFormat; kind: 'criticos' | 'vitorias'; items: string[]; pageNumber: number; totalPages: number }) {
  const isWide = format === 'wide';
  const isCriticos = kind === 'criticos';

  const bg = isCriticos
    ? `linear-gradient(155deg, #1a2a08 0%, ${GENS.oliveDark} 60%, #2a3a14 100%)`
    : GENS.limeLight;
  const textColor = isCriticos ? GENS.white : GENS.oliveDark;
  const accent = isCriticos ? GENS.limeVivid : GENS.oliveDark;
  const title = isCriticos ? 'Pontos críticos' : 'Vitórias rápidas';
  const subtitle = isCriticos
    ? 'Resolva primeiro para destravar resultado'
    : 'Implemente esta semana para ganhos imediatos';

  return (
    <Slide format={format} background={bg} textColor={textColor} pageNumber={pageNumber} totalPages={totalPages}>
      <div
        style={{
          position: 'absolute',
          top: isWide ? 180 : 240,
          left: isWide ? 120 : 80,
          right: isWide ? 120 : 80,
          bottom: isWide ? 100 : 140,
        }}
      >
        <p style={{ fontSize: isWide ? 16 : 24, fontWeight: 600, color: accent, letterSpacing: '0.12em', marginBottom: 12 }}>
          {isCriticos ? 'O QUE PRECISA SER CORRIGIDO' : 'GANHOS RÁPIDOS DISPONÍVEIS'}
        </p>
        <h2 style={{ fontSize: isWide ? 88 : 120, fontWeight: 800, lineHeight: 1, marginBottom: 16, color: textColor }}>
          {title}
        </h2>
        <p style={{ fontSize: isWide ? 24 : 34, fontWeight: 400, opacity: 0.7, marginBottom: 48 }}>
          {subtitle}
        </p>

        <ul style={{ display: 'flex', flexDirection: 'column', gap: isWide ? 24 : 36, listStyle: 'none', padding: 0, margin: 0 }}>
          {items.slice(0, 6).map((item, i) => (
            <li
              key={i}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: isWide ? 20 : 28,
                fontSize: isWide ? 28 : 38,
                fontWeight: 600,
                lineHeight: 1.35,
              }}
            >
              <span style={{ color: accent, flexShrink: 0, fontSize: isWide ? 32 : 44 }}>→</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </Slide>
  );
}

function DimensionSlide({ dimension, format, pageNumber, totalPages }: { dimension: SeoStructuredDimension; format: ReportFormat; pageNumber: number; totalPages: number }) {
  const isWide = format === 'wide';
  const status = dimension.status as SeoDimensionStatus;
  const meta = statusMeta[status] ?? statusMeta.atencao;
  const negatives = (dimension.pontos_negativos ?? []).slice(0, isWide ? 3 : 3);
  const improvements = (dimension.melhorias_sugeridas ?? []).slice(0, isWide ? 3 : 3);

  return (
    <Slide format={format} background={GENS.white} textColor={GENS.oliveDark} pageNumber={pageNumber} totalPages={totalPages}>
      {/* Decorative side stripe */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: isWide ? 16 : 22,
          background: GENS.limeVivid,
        }}
      />

      <div
        style={{
          position: 'absolute',
          top: isWide ? 140 : 200,
          left: isWide ? 96 : 80,
          right: isWide ? 96 : 80,
          bottom: isWide ? 80 : 120,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: isWide ? 20 : 28, marginBottom: 24 }}>
          <span
            style={{
              display: 'inline-block',
              background: meta.bg,
              color: meta.color,
              padding: isWide ? '6px 16px' : '10px 22px',
              borderRadius: 6,
              fontSize: isWide ? 14 : 20,
              fontWeight: 700,
              letterSpacing: '0.08em',
            }}
          >
            {meta.label}
          </span>
          <span style={{ fontSize: isWide ? 16 : 22, fontWeight: 600, opacity: 0.5, letterSpacing: '0.08em' }}>
            DIMENSÃO
          </span>
        </div>

        <h2 style={{ fontSize: isWide ? 72 : 96, fontWeight: 800, lineHeight: 1.05, marginBottom: isWide ? 48 : 64, color: GENS.oliveDark }}>
          {dimensionTitle(dimension.dimensao)}
        </h2>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isWide ? '1fr 1fr' : '1fr',
            gap: isWide ? 48 : 48,
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* Pontos negativos */}
          {negatives.length > 0 && (
            <div>
              <p style={{ fontSize: isWide ? 14 : 20, fontWeight: 700, color: GENS.red, letterSpacing: '0.12em', marginBottom: 16 }}>
                O QUE ESTÁ PREJUDICANDO
              </p>
              <ul style={{ display: 'flex', flexDirection: 'column', gap: isWide ? 18 : 28, listStyle: 'none', padding: 0, margin: 0 }}>
                {negatives.map((neg, i) => (
                  <li
                    key={i}
                    style={{
                      padding: isWide ? '14px 16px' : '20px 24px',
                      background: '#FEF2F2',
                      borderLeft: `3px solid ${GENS.red}`,
                      borderRadius: 4,
                    }}
                  >
                    <p style={{ fontSize: isWide ? 18 : 26, fontWeight: 700, color: GENS.oliveDark, lineHeight: 1.3, marginBottom: 6 }}>
                      {neg.problema}
                    </p>
                    {neg.impacto_no_ranking && (
                      <p style={{ fontSize: isWide ? 14 : 20, fontWeight: 400, color: '#4A5568', lineHeight: 1.4 }}>
                        {neg.impacto_no_ranking}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Melhorias sugeridas */}
          {improvements.length > 0 && (
            <div>
              <p style={{ fontSize: isWide ? 14 : 20, fontWeight: 700, color: GENS.oliveDark, letterSpacing: '0.12em', marginBottom: 16 }}>
                O QUE FAZER
              </p>
              <ul style={{ display: 'flex', flexDirection: 'column', gap: isWide ? 18 : 28, listStyle: 'none', padding: 0, margin: 0 }}>
                {improvements.map((imp, i) => (
                  <li
                    key={i}
                    style={{
                      padding: isWide ? '14px 16px' : '20px 24px',
                      background: GENS.limeLight,
                      borderLeft: `3px solid ${GENS.oliveDark}`,
                      borderRadius: 4,
                    }}
                  >
                    <p style={{ fontSize: isWide ? 18 : 26, fontWeight: 700, color: GENS.oliveDark, lineHeight: 1.3, marginBottom: 6 }}>
                      {imp.acao}
                    </p>
                    {imp.como_executar && (
                      <p style={{ fontSize: isWide ? 14 : 20, fontWeight: 400, color: '#4A5568', lineHeight: 1.4 }}>
                        {imp.como_executar}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </Slide>
  );
}

function RoadmapSlide({ roadmap, format, pageNumber, totalPages }: { roadmap: NonNullable<SeoStructuredAnalysis['roadmap_priorizado']>; format: ReportFormat; pageNumber: number; totalPages: number }) {
  const isWide = format === 'wide';
  const weeks = (['semana_1', 'semana_2', 'semana_3', 'semana_4'] as const).map((key, idx) => ({
    label: `Semana ${idx + 1}`,
    items: roadmap[key] ?? [],
  }));

  return (
    <Slide
      format={format}
      background={`linear-gradient(155deg, #1a2a08 0%, ${GENS.oliveDark} 60%, ${GENS.oliveMid} 100%)`}
      textColor={GENS.white}
      pageNumber={pageNumber}
      totalPages={totalPages}
    >
      <div
        style={{
          position: 'absolute',
          top: isWide ? 140 : 200,
          left: isWide ? 96 : 80,
          right: isWide ? 96 : 80,
          bottom: isWide ? 100 : 140,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <p style={{ fontSize: isWide ? 16 : 24, fontWeight: 600, color: GENS.limeVivid, letterSpacing: '0.12em', marginBottom: 12 }}>
          PLANO DE AÇÃO
        </p>
        <h2 style={{ fontSize: isWide ? 80 : 108, fontWeight: 800, lineHeight: 1.05, marginBottom: 16 }}>
          Próximas <span style={{ color: GENS.limeVivid }}>4 semanas</span>
        </h2>
        <p style={{ fontSize: isWide ? 22 : 32, fontWeight: 400, opacity: 0.7, marginBottom: isWide ? 56 : 80 }}>
          O que executar a cada semana para destravar resultado
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isWide ? 'repeat(4, 1fr)' : '1fr',
            gap: isWide ? 24 : 32,
            flex: 1,
          }}
        >
          {weeks.map((week, idx) => (
            <div
              key={week.label}
              style={{
                background: 'rgba(255,255,255,0.06)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(213, 233, 70, 0.25)',
                borderRadius: 12,
                padding: isWide ? '24px 20px' : '32px 28px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: isWide ? 44 : 60,
                  height: isWide ? 44 : 60,
                  borderRadius: 999,
                  background: GENS.limeVivid,
                  color: GENS.oliveDark,
                  fontSize: isWide ? 18 : 26,
                  fontWeight: 800,
                  marginBottom: 16,
                }}
              >
                {idx + 1}
              </div>
              <p style={{ fontSize: isWide ? 20 : 28, fontWeight: 800, color: GENS.limeVivid, marginBottom: 16 }}>
                {week.label}
              </p>
              <ul style={{ display: 'flex', flexDirection: 'column', gap: isWide ? 10 : 16, listStyle: 'none', padding: 0, margin: 0, flex: 1 }}>
                {week.items.slice(0, 4).map((item, i) => (
                  <li key={i} style={{ display: 'flex', gap: 8, fontSize: isWide ? 15 : 22, fontWeight: 400, lineHeight: 1.4, color: 'rgba(255,255,255,0.9)' }}>
                    <span style={{ color: GENS.limeVivid, flexShrink: 0 }}>→</span>
                    <span>{item}</span>
                  </li>
                ))}
                {week.items.length === 0 && (
                  <li style={{ fontSize: isWide ? 15 : 22, fontWeight: 400, color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>
                    sem ações nesta semana
                  </li>
                )}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </Slide>
  );
}

function CtaSlide({ format, pageNumber, totalPages }: { format: ReportFormat; pageNumber: number; totalPages: number }) {
  const isWide = format === 'wide';
  return (
    <Slide format={format} background={GENS.limeVivid} textColor={GENS.oliveDark} pageNumber={pageNumber} totalPages={totalPages}>
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: isWide ? 120 : 80,
          right: isWide ? 120 : 80,
          transform: 'translateY(-50%)',
        }}
      >
        <p style={{ fontSize: isWide ? 18 : 26, fontWeight: 700, color: GENS.oliveDark, letterSpacing: '0.12em', marginBottom: 24 }}>
          PRÓXIMO PASSO
        </p>
        <h2 style={{ fontSize: isWide ? 104 : 128, fontWeight: 800, lineHeight: 1.02, color: GENS.oliveDark, marginBottom: 32 }}>
          Pronto para
          <br />
          <span style={{ textDecoration: 'underline', textDecorationThickness: isWide ? 8 : 12 }}>implementar?</span>
        </h2>
        <p style={{ fontSize: isWide ? 28 : 40, fontWeight: 400, color: GENS.oliveDark, lineHeight: 1.4, marginBottom: 48, maxWidth: isWide ? 1000 : '100%' }}>
          A Gens executa o plano com você — do Google Meu Negócio à automação de captação de pacientes.
        </p>

        <div style={{ display: 'flex', flexDirection: isWide ? 'row' : 'column', gap: isWide ? 24 : 32, alignItems: isWide ? 'center' : 'flex-start' }}>
          <span
            style={{
              display: 'inline-block',
              background: GENS.oliveDark,
              color: GENS.limeVivid,
              padding: isWide ? '20px 36px' : '28px 48px',
              borderRadius: 8,
              fontSize: isWide ? 24 : 34,
              fontWeight: 800,
            }}
          >
            Fale com a Gens ↗
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <p style={{ fontSize: isWide ? 20 : 28, fontWeight: 700, color: GENS.oliveDark }}>@agencia.gens</p>
            <p style={{ fontSize: isWide ? 18 : 26, fontWeight: 400, color: GENS.oliveDark, opacity: 0.7 }}>João Pessoa · marketing para clínicas</p>
          </div>
        </div>
      </div>
    </Slide>
  );
}

export function SeoProfileReport({ analysis, format, clinicName }: SeoProfileReportProps) {
  const structured = analysis.extracted_signals?.structuredAnalysis ?? null;
  const dimensions = (structured?.analise_por_dimensao ?? []).slice().sort((a, b) => {
    const order: Record<string, number> = { critico: 0, atencao: 1, ok: 2, excelente: 3 };
    return (order[a.status ?? ''] ?? 4) - (order[b.status ?? ''] ?? 4);
  });
  const criticos = structured?.resumo_executivo?.pontos_criticos?.filter(Boolean) ?? [];
  const vitorias = structured?.resumo_executivo?.vitorias_rapidas?.filter(Boolean) ?? [];
  const roadmap = structured?.roadmap_priorizado ?? null;
  const hasRoadmap = roadmap && (
    (roadmap.semana_1?.length ?? 0) +
    (roadmap.semana_2?.length ?? 0) +
    (roadmap.semana_3?.length ?? 0) +
    (roadmap.semana_4?.length ?? 0)
  ) > 0;

  // Build slide list to compute totalPages
  const slides: Array<{ key: string; render: (page: number, total: number) => React.ReactNode }> = [];
  slides.push({ key: 'cover', render: (p, t) => <CoverSlide analysis={analysis} format={format} clinicName={clinicName} pageNumber={p} totalPages={t} /> });
  if (criticos.length > 0) {
    slides.push({ key: 'criticos', render: (p, t) => <HighlightSlide format={format} kind="criticos" items={criticos} pageNumber={p} totalPages={t} /> });
  }
  if (vitorias.length > 0) {
    slides.push({ key: 'vitorias', render: (p, t) => <HighlightSlide format={format} kind="vitorias" items={vitorias} pageNumber={p} totalPages={t} /> });
  }
  dimensions.forEach((dim, idx) => {
    slides.push({ key: `dim-${idx}-${dim.dimensao}`, render: (p, t) => <DimensionSlide dimension={dim} format={format} pageNumber={p} totalPages={t} /> });
  });
  if (hasRoadmap && roadmap) {
    slides.push({ key: 'roadmap', render: (p, t) => <RoadmapSlide roadmap={roadmap} format={format} pageNumber={p} totalPages={t} /> });
  }
  slides.push({ key: 'cta', render: (p, t) => <CtaSlide format={format} pageNumber={p} totalPages={t} /> });

  const total = slides.length;

  return (
    <div className="seo-report-root">
      {slides.map((slide, i) => (
        <div key={slide.key}>{slide.render(i + 1, total)}</div>
      ))}
    </div>
  );
}
